import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, X, User } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { PricingCards } from '../components/landing/PricingCards';
export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    shopName: '',
    cnpj: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    plan: 'pro'
  });
  const [team, setTeam] = useState([{
    id: '1',
    name: 'Você',
    email: 'contato@oficina.com',
    role: 'Gerente'
  }]);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    role: 'Mecânico'
  });
  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleFinish = () => {
    navigate('/app');
  };
  const addTeamMember = () => {
    if (newMember.name && newMember.email) {
      setTeam([...team, {
        ...newMember,
        id: Date.now().toString()
      }]);
      setNewMember({
        name: '',
        email: '',
        role: 'Mecânico'
      });
    }
  };
  const removeTeamMember = (id: string) => {
    setTeam(team.filter(m => m.id !== id));
  };
  const isStep1Valid = formData.shopName.length > 2 && formData.cnpj.length > 10;
  return <div className="min-h-screen bg-background flex flex-col" data-id="element-707">
      {/* Header */}
      <header className="h-20 px-6 flex items-center justify-between shrink-0" data-id="element-708">
        <div className="flex items-center gap-2" data-id="element-709">
          <div className="bg-primary p-1.5 rounded-lg" data-id="element-710">
            <Wrench className="h-5 w-5 text-primary-foreground" data-id="element-711" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight" data-id="element-712">
            GarageFlow
          </span>
        </div>
        <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-id="element-713">
          Sair
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6" data-id="element-714">
        <div className="w-full max-w-2xl" data-id="element-715">
          {/* Progress Indicator */}
          <div className="mb-8 flex flex-col items-center" data-id="element-716">
            <div className="flex items-center gap-3 mb-3" data-id="element-717">
              {[1, 2, 3].map(i => <div key={i} className={`h-2 w-16 rounded-full transition-colors duration-300 ${i <= step ? 'bg-primary' : 'bg-muted'}`} data-id="element-718" />)}
            </div>
            <span className="text-sm font-medium text-muted-foreground" data-id="element-719">
              Passo {step} de 3 —{' '}
              {step === 1 ? 'Dados da Oficina' : step === 2 ? 'Sua Equipe' : 'Escolha seu Plano'}
            </span>
          </div>

          <div className="relative overflow-hidden bg-card border border-border rounded-2xl shadow-xl" data-id="element-720">
            <AnimatePresence mode="wait" data-id="element-721">
              {step === 1 && <motion.div key="step1" initial={{
              x: 20,
              opacity: 0
            }} animate={{
              x: 0,
              opacity: 1
            }} exit={{
              x: -20,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="p-8 md:p-10" data-id="element-722">
                  <div className="mb-8 text-center" data-id="element-723">
                    <h1 className="text-3xl font-heading font-bold mb-2" data-id="element-724">
                      Vamos configurar sua oficina
                    </h1>
                    <p className="text-muted-foreground" data-id="element-725">
                      Preencha os dados básicos para começarmos.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-726">
                    <div className="space-y-2" data-id="element-727">
                      <Label htmlFor="shopName" data-id="element-728">Nome da oficina *</Label>
                      <Input id="shopName" value={formData.shopName} onChange={e => setFormData({
                    ...formData,
                    shopName: e.target.value
                  })} placeholder="Ex: Auto Center Premium" className="h-12" data-id="element-729" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-730">
                      <div className="space-y-2" data-id="element-731">
                        <Label htmlFor="cnpj" data-id="element-732">CNPJ *</Label>
                        <Input id="cnpj" value={formData.cnpj} onChange={e => setFormData({
                      ...formData,
                      cnpj: e.target.value
                    })} placeholder="00.000.000/0001-00" className="h-12" data-id="element-733" />
                      </div>
                      <div className="space-y-2" data-id="element-734">
                        <Label htmlFor="phone" data-id="element-735">Telefone</Label>
                        <Input id="phone" value={formData.phone} onChange={e => setFormData({
                      ...formData,
                      phone: e.target.value
                    })} placeholder="(00) 00000-0000" className="h-12" data-id="element-736" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-737">
                      <Label htmlFor="address" data-id="element-738">Endereço completo</Label>
                      <Input id="address" value={formData.address} onChange={e => setFormData({
                    ...formData,
                    address: e.target.value
                  })} placeholder="Rua, número, bairro" className="h-12" data-id="element-739" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-740">
                      <div className="space-y-2" data-id="element-741">
                        <Label htmlFor="city" data-id="element-742">Cidade</Label>
                        <Input id="city" value={formData.city} onChange={e => setFormData({
                      ...formData,
                      city: e.target.value
                    })} placeholder="Sua cidade" className="h-12" data-id="element-743" />
                      </div>
                      <div className="space-y-2" data-id="element-744">
                        <Label htmlFor="state" data-id="element-745">Estado</Label>
                        <Input id="state" value={formData.state} onChange={e => setFormData({
                      ...formData,
                      state: e.target.value
                    })} placeholder="UF" className="h-12" data-id="element-746" />
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 flex justify-end" data-id="element-747">
                    <Button size="lg" onClick={handleNext} disabled={!isStep1Valid} className="px-8" data-id="element-748">
                      Continuar
                    </Button>
                  </div>
                </motion.div>}

              {step === 2 && <motion.div key="step2" initial={{
              x: 20,
              opacity: 0
            }} animate={{
              x: 0,
              opacity: 1
            }} exit={{
              x: -20,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="p-8 md:p-10" data-id="element-749">
                  <div className="mb-8 text-center" data-id="element-750">
                    <h1 className="text-3xl font-heading font-bold mb-2" data-id="element-751">
                      Quem vai trabalhar no GarageFlow?
                    </h1>
                    <p className="text-muted-foreground" data-id="element-752">
                      Adicione mecânicos e atendentes. Você pode adicionar mais
                      depois.
                    </p>
                  </div>

                  <div className="bg-muted/30 p-5 rounded-xl border border-border mb-8" data-id="element-753">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-id="element-754">
                      <div className="md:col-span-4 space-y-2" data-id="element-755">
                        <Label className="text-xs" data-id="element-756">Nome</Label>
                        <Input placeholder="João Silva" value={newMember.name} onChange={e => setNewMember({
                      ...newMember,
                      name: e.target.value
                    })} data-id="element-757" />
                      </div>
                      <div className="md:col-span-4 space-y-2" data-id="element-758">
                        <Label className="text-xs" data-id="element-759">E-mail</Label>
                        <Input placeholder="joao@oficina.com" value={newMember.email} onChange={e => setNewMember({
                      ...newMember,
                      email: e.target.value
                    })} data-id="element-760" />
                      </div>
                      <div className="md:col-span-3 space-y-2" data-id="element-761">
                        <Label className="text-xs" data-id="element-762">Função</Label>
                        <Select value={newMember.role} onValueChange={v => setNewMember({
                      ...newMember,
                      role: v
                    })} data-id="element-763">
                          <SelectTrigger data-id="element-764">
                            <SelectValue data-id="element-765" />
                          </SelectTrigger>
                          <SelectContent data-id="element-766">
                            <SelectItem value="Mecânico" data-id="element-767">Mecânico</SelectItem>
                            <SelectItem value="Atendente" data-id="element-768">Atendente</SelectItem>
                            <SelectItem value="Gerente" data-id="element-769">Gerente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-1" data-id="element-770">
                        <Button type="button" variant="secondary" className="w-full" onClick={addTeamMember} data-id="element-771">
                          <User className="h-4 w-4" data-id="element-772" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-773">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3" data-id="element-774">
                      Equipe adicionada ({team.length})
                    </h3>
                    {team.map(member => <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-background" data-id="element-775">
                        <div className="flex items-center gap-3" data-id="element-776">
                          <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium text-xs" data-id="element-777">
                            {member.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div data-id="element-778">
                            <p className="text-sm font-medium leading-none" data-id="element-779">
                              {member.name}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1" data-id="element-780">
                              {member.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-781">
                          <Badge variant="outline" className="bg-muted" data-id="element-782">
                            {member.role}
                          </Badge>
                          {member.id !== '1' && <button onClick={() => removeTeamMember(member.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-id="element-783">
                              <X className="h-4 w-4" data-id="element-784" />
                            </button>}
                        </div>
                      </div>)}
                  </div>

                  <div className="mt-10 flex items-center justify-between" data-id="element-785">
                    <button onClick={handleNext} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-id="element-786">
                      Pular esta etapa
                    </button>
                    <div className="flex gap-3" data-id="element-787">
                      <Button variant="outline" onClick={handleBack} data-id="element-788">
                        Voltar
                      </Button>
                      <Button onClick={handleNext} className="px-8" data-id="element-789">
                        Continuar
                      </Button>
                    </div>
                  </div>
                </motion.div>}

              {step === 3 && <motion.div key="step3" initial={{
              x: 20,
              opacity: 0
            }} animate={{
              x: 0,
              opacity: 1
            }} exit={{
              x: -20,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="p-8 md:p-10" data-id="element-790">
                  <div className="mb-8 text-center" data-id="element-791">
                    <h1 className="text-3xl font-heading font-bold mb-2" data-id="element-792">
                      Escolha o plano ideal
                    </h1>
                    <p className="text-muted-foreground" data-id="element-793">
                      Comece com 14 dias grátis. Cancele quando quiser.
                    </p>
                  </div>

                  <div className="-mx-4 md:mx-0" data-id="element-794">
                    <PricingCards selectedPlan={formData.plan} onSelect={planId => setFormData({
                  ...formData,
                  plan: planId
                })} ctaLabel="Selecionar" data-id="element-795" />
                  </div>

                  <div className="mt-10 flex items-center justify-between" data-id="element-796">
                    <Button variant="outline" onClick={handleBack} data-id="element-797">
                      Voltar
                    </Button>
                    <Button size="lg" onClick={handleFinish} className="px-8" data-id="element-798">
                      Começar 14 dias grátis
                    </Button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}